import { useEffect, useRef, useState } from "react";
import type { VideoItem } from "@/mocks/videos";

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function PlayerDemo({ video }: { video: VideoItem }) {
  const totalSeconds = 100;
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [video.id]);

  useEffect(() => {
    if (playing) {
      timerRef.current = window.setInterval(() => {
        setProgress((prev) => {
          if (prev >= totalSeconds) {
            setPlaying(false);
            return totalSeconds;
          }
          return prev + 0.5;
        });
      }, 100);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing, totalSeconds]);

  const toggle = () => {
    if (progress >= totalSeconds) setProgress(0);
    setPlaying((v) => !v);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-background-200 bg-foreground-950">
      <div className="relative w-full aspect-video">
        <img
          src={video.backdrop}
          alt={`${video.title} 播放画面`}
          className="w-full h-full object-cover object-top"
        />
        <div className={`absolute inset-0 bg-foreground-950/45 transition-opacity ${playing ? "opacity-100" : "opacity-60"}`}></div>

        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-accent-500 text-white text-xs font-medium flex items-center gap-1">
          <i className="ri-information-line"></i>
          演示播放器 · 非真实播放
        </span>

        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "暂停" : "播放"}
          className="absolute inset-0 flex items-center justify-center group"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white group-hover:bg-primary-500 transition-colors">
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl md:text-4xl`}></i>
          </span>
        </button>
      </div>

      <div className="bg-foreground-950 px-4 py-3">
        <div className="relative h-1.5 rounded-full bg-white/20 cursor-pointer">
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-primary-500 transition-all"
            style={{ width: `${(progress / totalSeconds) * 100}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between mt-3 text-white/80 text-xs md:text-sm">
          <div className="flex items-center gap-4">
            <button type="button" onClick={toggle} className="hover:text-white" aria-label="播放暂停">
              <i className={`${playing ? "ri-pause-line" : "ri-play-line"} text-xl`}></i>
            </button>
            <span className="hover:text-white"><i className="ri-volume-up-line text-lg"></i></span>
            <span className="tabular-nums">
              {formatTime((progress / totalSeconds) * 90)} / 01:30
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hover:text-white cursor-pointer hidden sm:inline"><i className="ri-hd-line text-lg"></i></span>
            <span className="hover:text-white cursor-pointer"><i className="ri-fullscreen-line text-lg"></i></span>
          </div>
        </div>
      </div>
    </div>
  );
}